import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd'
import { ActionItem, User } from '../../types'
import Modal from '../shared/Modal'
import PriorityDropdown from '../topics/PriorityDropdown'
import CategoryDropdown from '../topics/CategoryDropdown'
import ActionItemChecklist from '../action_items/ActionItemChecklist'
import ConfirmModal from '../shared/ConfirmModal'
import ActionItemHistory from '../action_items/ActionItemHistory'
import ActionItemFilterBar, { type ActionItemSortOption } from '../action_items/ActionItemFilterBar'
import {
  ACTION_ITEM_ROW_GRID,
  ActionItemCreatedDateCell,
  ActionItemDueDateCell,
  ActionItemOwnerCell,
  ActionItemSourceCell,
  ActionItemTableHeader,
  ActionItemTitleCell,
  actionItemDueDateStyle,
  formatActionItemDate,
} from '../action_items/ActionItemTable'
import { comparePeople, sortPeople } from '../../utils/peopleSort'

interface Props {
  action_items: ActionItem[]
  completedItems: ActionItem[]
  meetingId: number
  teamId?: number
  members?: User[]
  actionItemMembers?: User[]
  readOnly?: boolean
  canCreate?: boolean
}

// Task #700: the sort select mirrors the Team Action Items page's options.
type SortMode =
  | ''
  | 'manual'
  | 'owner_asc'
  | 'owner_desc'
  | 'due_date_asc'
  | 'due_date_desc'
  | 'created_at_desc'
  | 'created_at_asc'

const SORT_OPTIONS: ActionItemSortOption[] = [
  { value: '', label: 'Default Sort' },
  { value: 'manual', label: 'Manual (drag to reorder)' },
  { value: 'owner_asc', label: 'Owner (A-Z)' },
  { value: 'owner_desc', label: 'Owner (Z-A)' },
  { value: 'due_date_asc', label: 'Due Date (Earliest)' },
  { value: 'due_date_desc', label: 'Due Date (Latest)' },
  { value: 'created_at_desc', label: 'Created (Newest)' },
  { value: 'created_at_asc', label: 'Created (Oldest)' },
]

const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

// Mirrors the server's due-date semantics: undated items never match a date
// range, "this week" runs from today through the coming Sunday, and a
// completed item is never overdue or due — ActionItem#overdue? / #due_today?
// and the overdue / due_this_week scopes all restrict themselves to open and
// carried-over items.
function matchesDueDateRange(item: ActionItem, range: string): boolean {
  if (!range) return true
  if (item.status === 'completed') return false
  if (!item.due_date) return false
  const due = new Date(item.due_date + 'T00:00:00')
  if (Number.isNaN(due.getTime())) return false
  const today = startOfToday()
  switch (range) {
    case 'overdue':
      return due.getTime() < today.getTime()
    case 'today':
      return due.getTime() === today.getTime()
    case 'this_week': {
      const endOfWeek = new Date(today)
      endOfWeek.setDate(endOfWeek.getDate() + ((7 - today.getDay()) % 7))
      return due.getTime() >= today.getTime() && due.getTime() <= endOfWeek.getTime()
    }
    default:
      return true
  }
}

export default function SectionAccountability({ action_items, completedItems, meetingId, teamId, members, actionItemMembers, readOnly, canCreate = false }: Props) {
  // Guides are assignable owners for action items, so the create-item owner
  // picker uses actionItemMembers (which includes guides). The convert-to-topic
  // owner picker below stays on members (topics exclude guides).
  const ownerMembers = actionItemMembers ?? members
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [showCompleted, setShowCompleted] = useState(true)
  const [topicModal, setTopicModal] = useState<{ open: boolean; item: ActionItem | null }>({ open: false, item: null })
  const [topicTitle, setTopicTitle] = useState('')
  const [topicDescription, setTopicDescription] = useState('')
  const [topicCategory, setTopicCategory] = useState('')
  const [topicOwnerId, setTopicOwnerId] = useState<string>('')
  const [topicType, setTopicType] = useState('short_term')
  const [topicPriority, setTopicPriority] = useState(false)
  const [topicSubmitting, setTopicSubmitting] = useState(false)
  const [removeModal, setRemoveModal] = useState<{ open: boolean; item: ActionItem | null }>({ open: false, item: null })
  const [removeSubmitting, setRemoveSubmitting] = useState(false)
  const [carryOverModal, setCarryOverModal] = useState<{ open: boolean; item: ActionItem | null }>({ open: false, item: null })
  const [carryOverDate, setCarryOverDate] = useState('')
  const [carryOverSubmitting, setCarryOverSubmitting] = useState(false)
  // Task #478: four-way sort control. Task #697: the default groups items by
  // owner (A-Z) and orders each group by due date; Manual orders by the
  // persisted position and enables dragging. Task #700: the control now lives
  // in the filter bar and carries the team page's option list.
  const [sortMode, setSortMode] = useState<SortMode>('')

  // Task #700: client-side filters over the props the section already has —
  // changing one never navigates or reloads the meeting.
  const [search, setSearch] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const [dueDateFilter, setDueDateFilter] = useState('')

  const hasActiveFilters = Boolean(search.trim() || ownerFilter || dueDateFilter)

  const matchesFilters = (item: ActionItem) => {
    const term = search.trim().toLowerCase()
    if (term && !item.title.toLowerCase().includes(term)) return false
    if (ownerFilter && String(item.owner?.id ?? '') !== ownerFilter) return false
    if (!matchesDueDateRange(item, dueDateFilter)) return false
    return true
  }

  const clearFilters = () => {
    setSearch('')
    setOwnerFilter('')
    setDueDateFilter('')
  }

  // Task #613: owner ordering uses the shared people comparator.
  const byOwnerName = (a: ActionItem, b: ActionItem) => comparePeople(a.owner, b.owner)

  // Task #799: the server groups an owner's items together through
  // `User.alphabetical_order`, which tie-breaks two identically named owners
  // on users.id; the shared comparator tie-breaks on email. Keep the default
  // ordering identical to the Team Action Items page by falling back to the
  // owner's id, so an owner's rows always stay in one contiguous group.
  const byOwner = (a: ActionItem, b: ActionItem) =>
    byOwnerName(a, b) || (a.owner?.id ?? 0) - (b.owner?.id ?? 0)

  // Undated items sort after dated ones.
  const dueTime = (item: ActionItem) => {
    const time = item.due_date ? new Date(item.due_date).getTime() : NaN
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time
  }
  const byDueDate = (a: ActionItem, b: ActionItem) => dueTime(a) - dueTime(b)

  // Task #697: default ordering — owner A-Z, then soonest due date first
  // within each owner's group (undated last), with a stable id tie-break.
  // Task #799: open and carried-over items share this one ordering, matching
  // the Team Action Items page for the same set of items.
  const byDefault = (a: ActionItem, b: ActionItem) =>
    byOwner(a, b) || byDueDate(a, b) || a.id - b.id

  const byPosition = (a: ActionItem, b: ActionItem) => {
    const ap = a.position ?? Number.MAX_SAFE_INTEGER
    const bp = b.position ?? Number.MAX_SAFE_INTEGER
    return ap - bp || a.id - b.id
  }

  const byCreatedAt = (a: ActionItem, b: ActionItem) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()

  const bySortMode = (a: ActionItem, b: ActionItem): number => {
    switch (sortMode) {
      case 'manual': return byPosition(a, b)
      case 'owner_asc': return byOwnerName(a, b)
      case 'owner_desc': return byOwnerName(b, a)
      case 'due_date_asc': return byDueDate(a, b)
      // Undated items stay last in both directions.
      case 'due_date_desc': return byDueDate(b, a)
      case 'created_at_asc': return byCreatedAt(a, b)
      case 'created_at_desc': return byCreatedAt(b, a)
      default: return 0
    }
  }

  const arrange = (items: ActionItem[]) =>
    items.filter(matchesFilters).sort((a, b) => (sortMode ? bySortMode(a, b) : byDefault(a, b)))

  // Completed rows follow the same sort control, except for Manual: dragging
  // only reorders the active rows, so completed items fall back to the
  // default owner/due-date ordering there.
  const arrangeCompleted = (items: ActionItem[]) =>
    items
      .filter(matchesFilters)
      .sort((a, b) => (sortMode && sortMode !== 'manual' ? bySortMode(a, b) : byDefault(a, b)))

  // Task #799: open and carried-over items are one list — a carried-over row
  // sits inside its owner's group, marked by the inline "Carried Over" badge,
  // rather than being split into a group of its own.
  const activeItems = arrange(
    [...action_items].filter(i => i.status === 'open' || i.status === 'carried_over'),
  )
  const visibleCompletedItems = arrangeCompleted([...completedItems])

  const canDrag = sortMode === 'manual' && !readOnly && !!teamId

  // Task #583: auto-expand the top item. Facilitators disposition items
  // top-down, so whenever the identity of the top item changes (initial load,
  // a disposition reshuffling the list, or a sort-mode change) we expand it.
  // Keying off the top item's id (via a ref) means unrelated re-renders — the
  // 10s meeting heartbeat prop refresh, manual toggles, Collapse All — never
  // re-trigger it, so an explicit collapse of the current top item sticks.
  const topItemId = activeItems.length > 0 ? activeItems[0].id : null
  const prevTopItemIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (readOnly) return
    if (topItemId !== null && topItemId !== prevTopItemIdRef.current) {
      setExpandedIds(prev => {
        if (prev.has(topItemId)) return prev
        const next = new Set(prev)
        next.add(topItemId)
        return next
      })
    }
    prevTopItemIdRef.current = topItemId
  }, [topItemId, readOnly])

  const handleDragEnd = (result: DropResult) => {
    if (!canDrag || !result.destination || result.destination.index === result.source.index) return
    const next = Array.from(activeItems)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    router.put(`/teams/${teamId}/action_items/reorder`, {
      action_item_ids: next.map(i => i.id),
    }, { preserveScroll: true, preserveState: true })
  }

  const handleComplete = (item: ActionItem) => {
    router.post(`/meetings/${meetingId}/action_items/${item.id}/complete`, {}, { preserveScroll: true })
  }

  const handleRemove = (item: ActionItem) => {
    setRemoveModal({ open: true, item })
  }

  const handleRemoveConfirm = () => {
    if (!removeModal.item) return
    setRemoveSubmitting(true)
    router.delete(`/meetings/${meetingId}/action_items/${removeModal.item.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        setRemoveModal({ open: false, item: null })
        setRemoveSubmitting(false)
      },
      onError: () => {
        setRemoveSubmitting(false)
      },
    })
  }

  const handleRemoveModalClose = () => {
    setRemoveModal({ open: false, item: null })
    setRemoveSubmitting(false)
  }

  const handleUndoCarryOver = (item: ActionItem) => {
    router.post(`/meetings/${meetingId}/action_items/${item.id}/undo_carry_over`, {}, { preserveScroll: true })
  }

  const proposedCarryOverDate = (_item: ActionItem) => {
    const base = new Date()
    base.setDate(base.getDate() + 7)
    const y = base.getFullYear()
    const m = String(base.getMonth() + 1).padStart(2, '0')
    const d = String(base.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  const handleCarryOver = (item: ActionItem) => {
    setCarryOverDate(proposedCarryOverDate(item))
    setCarryOverModal({ open: true, item })
  }

  const handleCarryOverSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!carryOverModal.item || !carryOverDate) return
    setCarryOverSubmitting(true)
    router.post(`/meetings/${meetingId}/action_items/${carryOverModal.item.id}/carry_over`, {
      due_date: carryOverDate,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setCarryOverModal({ open: false, item: null })
        setCarryOverDate('')
        setCarryOverSubmitting(false)
      },
      onError: () => {
        setCarryOverSubmitting(false)
      },
    })
  }

  const handleCarryOverModalClose = () => {
    setCarryOverModal({ open: false, item: null })
    setCarryOverDate('')
    setCarryOverSubmitting(false)
  }

  const handleConvertToTopic = (item: ActionItem) => {
    setTopicTitle(item.title)
    setTopicDescription(item.description || '')
    setTopicCategory('')
    setTopicOwnerId(item.owner?.id ? String(item.owner.id) : '')
    setTopicType('short_term')
    setTopicPriority(false)
    setTopicModal({ open: true, item })
  }

  const handleTopicSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!topicModal.item || !topicTitle.trim()) return
    setTopicSubmitting(true)
    router.post(`/meetings/${meetingId}/action_items/${topicModal.item.id}/convert_to_topic`, {
      title: topicTitle.trim(),
      description: topicDescription,
      category: topicCategory,
      owner_id: topicOwnerId || null,
      topic_type: topicType,
      priority: topicPriority,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setTopicModal({ open: false, item: null })
        setTopicTitle('')
        setTopicDescription('')
        setTopicCategory('')
        setTopicOwnerId('')
        setTopicType('short_term')
        setTopicPriority(false)
        setTopicSubmitting(false)
      },
      onError: () => {
        setTopicSubmitting(false)
      },
    })
  }

  const handleTopicModalClose = () => {
    setTopicModal({ open: false, item: null })
    setTopicTitle('')
    setTopicDescription('')
    setTopicCategory('')
    setTopicOwnerId('')
    setTopicType('short_term')
    setTopicPriority(false)
    setTopicSubmitting(false)
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !ownerId) return
    router.post(`/meetings/${meetingId}/action_items`, {
      action_item: {
        title: title.trim(),
        owner_id: ownerId,
        due_date: dueDate || undefined,
      }
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setTitle('')
        setOwnerId('')
        setDueDate('')
        setShowForm(false)
      }
    })
  }

  const resetCreateForm = () => {
    setShowForm(false)
    setTitle('')
    setOwnerId('')
    setDueDate('')
  }

  const isOverdue = (item: ActionItem) => {
    return Boolean(item.overdue || ((item.status === 'open' || item.status === 'carried_over') && item.due_date && new Date(item.due_date + 'T00:00:00') < new Date(new Date().toDateString())))
  }

  const totalLoaded = action_items.filter(i => i.status === 'open' || i.status === 'carried_over').length + completedItems.length
  const totalVisible = activeItems.length + visibleCompletedItems.length

  const toggleExpanded = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allExpanded = activeItems.length > 0 && activeItems.every(i => expandedIds.has(i.id))

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set())
    } else {
      setExpandedIds(new Set(activeItems.map(i => i.id)))
    }
  }

  const rowProps = (item: ActionItem) => ({
    item,
    meetingId,
    readOnly,
    expanded: expandedIds.has(item.id),
    onToggleExpanded: () => toggleExpanded(item.id),
    onComplete: handleComplete,
    onCarryOver: handleCarryOver,
    onConvertToTopic: handleConvertToTopic,
    onUndoCarryOver: handleUndoCarryOver,
    onRemove: handleRemove,
    isOverdue,
    ownerMembers,
    draggable: canDrag,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Accountability from Last Week</h3>
          <p className="text-base text-text-muted">Who does what, by when?</p>
        </div>
        {activeItems.length > 0 && (
          <button
            type="button"
            onClick={handleExpandCollapseAll}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-stone/20 text-sm font-medium text-text-muted hover:text-text-primary hover:border-stone/40 transition-colors flex-shrink-0"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            title={allExpanded ? 'Collapse all action items' : 'Expand all action items'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {allExpanded
                ? <><polyline points="7 10 12 5 17 10" /><polyline points="7 14 12 19 17 14" /></>
                : <><polyline points="7 5 12 10 17 5" /><polyline points="7 19 12 14 17 19" /></>}
            </svg>
            <span>{allExpanded ? 'Collapse all' : 'Expand all'}</span>
          </button>
        )}
      </div>

      {totalLoaded > 0 && (
        <ActionItemFilterBar
          search={search}
          onSearchChange={setSearch}
          ownerId={ownerFilter}
          onOwnerChange={setOwnerFilter}
          dueDateRange={dueDateFilter}
          onDueDateRangeChange={setDueDateFilter}
          sort={sortMode}
          onSortChange={value => setSortMode(value as SortMode)}
          owners={ownerMembers || []}
          sortOptions={SORT_OPTIONS}
          hasActiveFilters={hasActiveFilters}
          onClear={clearFilters}
        />
      )}

      {totalLoaded === 0 ? (
        <div className="bg-white border border-stone/20 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[rgba(21,83,46,0.1)] flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-[#15532E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1B1B1B' }}>
            No open action items from previous meetings.
          </p>
          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: 400, color: '#1B1B1B', marginTop: '4px' }}>
            Your team is starting fresh this week.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          {totalVisible > 0 && <ActionItemTableHeader />}

          {totalVisible === 0 && (
            <div className="p-10 text-center">
              <p className="text-text-secondary text-base">No items match your filters.</p>
              <button
                onClick={clearFilters}
                className="text-sm text-text-secondary hover:text-text-primary underline mt-1"
              >
                Clear filters
              </button>
            </div>
          )}

          {activeItems.length > 0 && (
            canDrag ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="accountability-items" direction="vertical">
                  {(dropProvided) => (
                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      {activeItems.map((item, index) => (
                        <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={snapshot.isDragging ? 'shadow-lg bg-white' : ''}
                            >
                              <MeetingActionItemRow {...rowProps(item)} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              activeItems.map(item => (
                <MeetingActionItemRow key={item.id} {...rowProps(item)} />
              ))
            )
          )}

          {visibleCompletedItems.length > 0 && (
            <>
              <GroupHeaderRow
                open={showCompleted}
                onToggle={() => setShowCompleted(!showCompleted)}
                label={`Completed (${visibleCompletedItems.length})`}
                color="#1B1B1B"
              />
              {showCompleted && visibleCompletedItems.map(item => (
                <CompletedActionItemRow
                  key={item.id}
                  item={item}
                  readOnly={readOnly}
                  onReopen={handleComplete}
                />
              ))}
            </>
          )}

          {!readOnly && canCreate && (
            <div className="px-4 py-3">
              <AddActionItemForm
                open={showForm}
                onOpen={() => setShowForm(true)}
                onCancel={resetCreateForm}
                onSubmit={handleCreate}
                title={title}
                onTitleChange={setTitle}
                ownerId={ownerId}
                onOwnerIdChange={setOwnerId}
                dueDate={dueDate}
                onDueDateChange={setDueDate}
                ownerMembers={ownerMembers}
              />
            </div>
          )}
        </div>
      )}

      {totalLoaded === 0 && !readOnly && canCreate && (
        <div>
          <AddActionItemForm
            open={showForm}
            onOpen={() => setShowForm(true)}
            onCancel={resetCreateForm}
            onSubmit={handleCreate}
            title={title}
            onTitleChange={setTitle}
            ownerId={ownerId}
            onOwnerIdChange={setOwnerId}
            dueDate={dueDate}
            onDueDateChange={setDueDate}
            ownerMembers={ownerMembers}
            standalone
          />
        </div>
      )}

      <Modal isOpen={topicModal.open} onClose={handleTopicModalClose} title="Add to The List">
        <form onSubmit={handleTopicSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Topic <span className="text-[#ED1C24]">*</span>
            </label>
            <input
              type="text"
              value={topicTitle}
              onChange={e => setTopicTitle(e.target.value)}
              required
              className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
              style={{ fontSize: '16px' }}
              placeholder="What needs to be discussed?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Description
            </label>
            <textarea
              value={topicDescription}
              onChange={e => setTopicDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
              style={{ fontSize: '16px' }}
              placeholder="Add more context (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Owner
            </label>
            <select
              value={topicOwnerId}
              onChange={e => setTopicOwnerId(e.target.value)}
              className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
              style={{ fontSize: '16px' }}
            >
              <option value="">Select owner</option>
              {sortPeople(members || []).map(m => (
                <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Type
            </label>
            <select
              value={topicType}
              onChange={e => setTopicType(e.target.value)}
              className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
              style={{ fontSize: '16px' }}
            >
              <option value="short_term">Short Term</option>
              <option value="long_term">Camp</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Category
            </label>
            <CategoryDropdown value={topicCategory} onChange={setTopicCategory} />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              Priority
            </label>
            <PriorityDropdown value={topicPriority} onChange={setTopicPriority} expanded />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleTopicModalClose}
              className="px-4 py-2 font-semibold text-text-primary hover:bg-[#351E1D]/5 rounded-lg transition-colors font-sans"
              style={{ fontSize: '16px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={topicSubmitting || !topicTitle.trim()}
              className="px-4 py-2 font-semibold text-white bg-[#ED1C24] hover:bg-pinnacle-red-dark rounded-lg transition-colors disabled:opacity-50 font-sans"
              style={{ fontSize: '16px' }}
            >
              {topicSubmitting ? 'Adding...' : 'Add Topic'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={removeModal.open}
        title="Remove Action Item"
        message={removeModal.item ? <>Are you sure you want to remove "{removeModal.item.title}"? This can't be undone.</> : ''}
        confirmLabel="Remove"
        variant="danger"
        submitting={removeSubmitting}
        onConfirm={handleRemoveConfirm}
        onClose={handleRemoveModalClose}
      />

      <Modal isOpen={carryOverModal.open} onClose={handleCarryOverModalClose} title="Carry Over Action Item">
        <form onSubmit={handleCarryOverSubmit} className="space-y-4">
          {carryOverModal.item && (
            <p className="text-text-primary font-sans" style={{ fontSize: '14px' }}>
              "{carryOverModal.item.title}" will be marked Carried Over with the due date below, and stays where it is in the list.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
              New due date <span className="text-[#ED1C24]">*</span>
            </label>
            <input
              type="date"
              value={carryOverDate}
              onChange={e => setCarryOverDate(e.target.value)}
              required
              className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCarryOverModalClose}
              className="px-4 py-2 font-semibold text-text-primary hover:bg-[#351E1D]/5 rounded-lg transition-colors font-sans"
              style={{ fontSize: '16px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={carryOverSubmitting || !carryOverDate}
              className="px-4 py-2 font-semibold text-white bg-[#ED1C24] hover:bg-pinnacle-red-dark rounded-lg transition-colors disabled:opacity-50 font-sans"
              style={{ fontSize: '16px' }}
            >
              {carryOverSubmitting ? 'Carrying Over...' : 'Confirm Carry Over'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

/** Inline "add action item" affordance, rendered as the table's last row. */
function AddActionItemForm({
  open, onOpen, onCancel, onSubmit, title, onTitleChange, ownerId, onOwnerIdChange,
  dueDate, onDueDateChange, ownerMembers, standalone = false,
}: {
  open: boolean
  onOpen: () => void
  onCancel: () => void
  onSubmit: (e: React.FormEvent) => void
  title: string
  onTitleChange: (value: string) => void
  ownerId: string
  onOwnerIdChange: (value: string) => void
  dueDate: string
  onDueDateChange: (value: string) => void
  ownerMembers?: User[]
  standalone?: boolean
}) {
  if (!open) {
    return (
      <button
        onClick={onOpen}
        className="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-text-primary transition-colors"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Action Item
      </button>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      className={standalone ? 'bg-white border border-stone/20 rounded-lg p-4 space-y-3' : 'space-y-3'}
    >
      <input
        type="text"
        value={title}
        onChange={e => onTitleChange(e.target.value)}
        placeholder="Action item..."
        className="w-full rounded-lg border border-stone/20 px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
        autoFocus
      />
      <div className="flex gap-3">
        <select
          value={ownerId}
          onChange={e => onOwnerIdChange(e.target.value)}
          className="flex-1 min-w-0 rounded-lg border border-stone/20 px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <option value="">Select owner...</option>
          {sortPeople(ownerMembers).map(m => (
            <option key={m.id} value={m.id}>{m.full_name}</option>
          ))}
        </select>
        <input
          type="date"
          value={dueDate}
          onChange={e => onDueDateChange(e.target.value)}
          className="rounded-lg border border-stone/20 px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!title.trim() || !ownerId}
          className="px-4 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-text-muted text-sm font-medium hover:text-text-primary"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

/** Full-width collapse toggle above the table's Completed group. */
function GroupHeaderRow({ open, onToggle, label, color }: {
  open: boolean
  onToggle: () => void
  label: string
  color: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-4 py-2 border-b border-t border-gray-200 bg-[#F8F9FA] text-left"
      style={{
        fontFamily: 'Montserrat, sans-serif',
        fontSize: '13px',
        fontWeight: 700,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      <svg
        style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        fill="none" viewBox="0 0 24 24" stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
      {label}
    </button>
  )
}

interface MeetingRowProps {
  item: ActionItem
  meetingId: number
  readOnly?: boolean
  expanded: boolean
  onToggleExpanded: () => void
  onComplete: (item: ActionItem) => void
  onCarryOver: (item: ActionItem) => void
  onConvertToTopic: (item: ActionItem) => void
  onUndoCarryOver: (item: ActionItem) => void
  onRemove: (item: ActionItem) => void
  isOverdue: (item: ActionItem) => boolean
  ownerMembers?: User[]
  draggable?: boolean
}

// Task #700: open and carried-over items render as rows of the same table the
// Team Action Items page uses, with the meeting's controls layered on: the
// complete checkbox, the expand chevron, inline title/owner/due-date editing,
// and the expanded edit panel underneath.
//
// Task #584 / #583 conventions still apply: fields save on blur/Enter and the
// editing flags are local, so the 10s meeting heartbeat prop refresh can't
// reset an in-progress edit.
function MeetingActionItemRow({
  item, meetingId, readOnly, expanded, onToggleExpanded, onComplete, onCarryOver,
  onConvertToTopic, onUndoCarryOver, onRemove, isOverdue, ownerMembers, draggable,
}: MeetingRowProps) {
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(item.title)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const titleSaving = useRef(false)

  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionValue, setDescriptionValue] = useState(item.description || '')
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null)
  const descriptionSaving = useRef(false)

  const [editingDueDate, setEditingDueDate] = useState(false)
  const [dueDateValue, setDueDateValue] = useState(item.due_date || '')
  const dueDateInputRef = useRef<HTMLInputElement>(null)
  const dueDateSaving = useRef(false)

  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false)
  const ownerAnchorRef = useRef<HTMLButtonElement>(null)

  const isCarriedOver = item.status === 'carried_over'
  const dueStyle = actionItemDueDateStyle(item)

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [editingTitle])

  useEffect(() => {
    if (!editingTitle) setTitleValue(item.title)
  }, [item.title, editingTitle])

  useEffect(() => {
    if (editingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus()
      const len = descriptionInputRef.current.value.length
      descriptionInputRef.current.setSelectionRange(len, len)
    }
  }, [editingDescription])

  useEffect(() => {
    if (!editingDescription) setDescriptionValue(item.description || '')
  }, [item.description, editingDescription])

  useEffect(() => {
    if (editingDueDate && dueDateInputRef.current) {
      dueDateInputRef.current.focus()
    }
  }, [editingDueDate])

  useEffect(() => {
    if (!editingDueDate) setDueDateValue(item.due_date || '')
  }, [item.due_date, editingDueDate])

  function saveField(attrs: Record<string, string | number | null>, onFinish?: () => void) {
    router.put(`/meetings/${meetingId}/action_items/${item.id}`, {
      action_item: attrs
    }, {
      preserveScroll: true,
      onFinish
    })
  }

  function handleTitleSave() {
    if (titleSaving.current || !editingTitle) return
    setEditingTitle(false)
    if (titleValue.trim() && titleValue.trim() !== item.title) {
      titleSaving.current = true
      saveField({ title: titleValue.trim() }, () => { titleSaving.current = false })
    } else {
      setTitleValue(item.title)
    }
  }

  function handleDescriptionSave() {
    if (descriptionSaving.current || !editingDescription) return
    setEditingDescription(false)
    const trimmed = descriptionValue.trim()
    if (trimmed !== (item.description || '')) {
      descriptionSaving.current = true
      saveField({ description: trimmed }, () => { descriptionSaving.current = false })
    } else {
      setDescriptionValue(item.description || '')
    }
  }

  function handleDueDateSave(nextValue: string) {
    if (dueDateSaving.current || !editingDueDate) return
    setEditingDueDate(false)
    if (nextValue !== (item.due_date || '')) {
      dueDateSaving.current = true
      saveField({ due_date: nextValue || null }, () => { dueDateSaving.current = false })
    } else {
      setDueDateValue(item.due_date || '')
    }
  }

  function handleOwnerSelect(memberId: number) {
    setOwnerMenuOpen(false)
    if (memberId !== item.owner?.id) {
      saveField({ owner_id: memberId })
    }
  }

  const canEditOwner = !readOnly && !!ownerMembers?.length

  return (
    <div data-action-item-row="true" id={`meeting-action-item-${item.id}`}>
      <div
        onClick={onToggleExpanded}
        className={`${ACTION_ITEM_ROW_GRID} py-2.5 border-b border-gray-100 transition-colors group cursor-pointer hover:bg-[#F8F9FA]`}
        style={{ borderLeft: isCarriedOver ? '3px solid #B8860B' : (dueStyle.borderLeft || 'none') }}
      >
        <input
          type="checkbox"
          checked={false}
          onClick={e => e.stopPropagation()}
          onChange={() => !readOnly && onComplete(item)}
          disabled={readOnly}
          aria-label={`Complete ${item.title}`}
          className="w-5 h-5 rounded border-stone/30 text-[#15532E] focus:ring-[#15532E]/20 flex-shrink-0 disabled:opacity-50"
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
        />

        <ActionItemTitleCell
          item={item}
          editing={editingTitle}
          value={titleValue}
          onChange={setTitleValue}
          onSave={handleTitleSave}
          onCancel={() => { setTitleValue(item.title); setEditingTitle(false) }}
          inputRef={titleInputRef}
          editable={!readOnly}
          onStartEdit={e => { if (!readOnly) { e.stopPropagation(); setEditingTitle(true) } }}
          onInputClick={e => e.stopPropagation()}
          leading={draggable ? (
            <span className="text-text-muted flex-shrink-0" title="Drag to reorder" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
                <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
                <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
              </svg>
            </span>
          ) : undefined}
        />

        <ActionItemOwnerCell
          item={item}
          members={ownerMembers || []}
          open={ownerMenuOpen}
          onToggle={e => { e.stopPropagation(); setOwnerMenuOpen(open => !open) }}
          onClose={() => setOwnerMenuOpen(false)}
          onSelect={handleOwnerSelect}
          triggerRef={ownerAnchorRef}
          editable={canEditOwner}
          triggerTitle="Change owner"
          menuOnClick={e => e.stopPropagation()}
        />

        <ActionItemDueDateCell
          item={item}
          editing={editingDueDate}
          value={dueDateValue}
          onChange={setDueDateValue}
          onSave={() => handleDueDateSave(dueDateValue)}
          onCancel={() => { setDueDateValue(item.due_date || ''); setEditingDueDate(false) }}
          inputRef={dueDateInputRef}
          editable={!readOnly}
          onStartEdit={e => { if (!readOnly) { e.stopPropagation(); setEditingDueDate(true) } }}
          onInputClick={e => e.stopPropagation()}
          inputAriaLabel="Due date"
          triggerTitle={readOnly ? undefined : 'Change due date'}
          emptyLabel={readOnly ? 'No due date' : 'Add due date'}
          onClear={readOnly ? undefined : () => handleDueDateSave('')}
        />

        <ActionItemCreatedDateCell item={item} />
        <ActionItemSourceCell item={item} />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onToggleExpanded() }}
            aria-label="Toggle action item details"
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-3 border-b border-gray-100 bg-[#FCFCFC]">
          {editingDescription ? (
            <div className="mb-3">
              <textarea
                ref={descriptionInputRef}
                value={descriptionValue}
                onChange={e => setDescriptionValue(e.target.value)}
                onBlur={handleDescriptionSave}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleDescriptionSave() }
                  if (e.key === 'Escape') { setDescriptionValue(item.description || ''); setEditingDescription(false) }
                }}
                rows={3}
                placeholder="Add details..."
                aria-label="Description"
                className="w-full text-sm text-text-primary border border-[#15532E]/40 rounded px-2 py-1.5 outline-none focus:border-[#15532E] resize-none"
              />
            </div>
          ) : item.description ? (
            <p
              onClick={() => { if (!readOnly) setEditingDescription(true) }}
              title={readOnly ? undefined : 'Edit description'}
              className={`text-sm text-text-primary mb-3 whitespace-pre-wrap ${readOnly ? '' : 'cursor-text hover:bg-stone/5 rounded -mx-1 px-1 transition-colors'}`}
            >
              {item.description}
            </p>
          ) : readOnly ? (
            <p className="text-sm text-text-muted italic mb-3">No description provided.</p>
          ) : (
            <p
              onClick={() => setEditingDescription(true)}
              className="text-sm text-text-muted italic mb-3 cursor-text hover:bg-stone/5 rounded -mx-1 px-1 transition-colors"
            >
              Add description
            </p>
          )}

          {isOverdue(item) && (
            <div className="flex items-center gap-1.5 text-sm text-status-off-track mb-3">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">Overdue — was due {formatActionItemDate(item.due_date)}</span>
            </div>
          )}

          <ActionItemChecklist
            actionItemId={item.id}
            items={item.checklist_items || []}
            readOnly={readOnly}
          />

          {!readOnly && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {isCarriedOver ? (
                <button
                  onClick={() => onUndoCarryOver(item)}
                  style={{
                    fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                    color: '#15532E', backgroundColor: 'rgba(21,83,46,0.15)',
                  }}
                  className="px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity"
                >
                  Undo
                </button>
              ) : (
                <>
                  <button
                    onClick={() => onComplete(item)}
                    style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                      color: '#15532E', backgroundColor: 'rgba(21,83,46,0.15)',
                    }}
                    className="px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity"
                  >
                    Complete
                  </button>
                  <button
                    onClick={() => onCarryOver(item)}
                    style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                      color: '#B8860B', backgroundColor: 'rgba(184,134,11,0.12)',
                    }}
                    className="px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity"
                  >
                    Carry Over
                  </button>
                  <button
                    onClick={() => onConvertToTopic(item)}
                    style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                      color: '#ED1C24', backgroundColor: 'rgba(237,28,36,0.12)',
                    }}
                    className="px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity"
                  >
                    → Topic
                  </button>
                </>
              )}
              <button
                onClick={() => onRemove(item)}
                style={{
                  fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                  color: '#1B1B1B', backgroundColor: 'rgba(27,27,27,0.10)',
                }}
                className="px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity"
              >
                Remove
              </button>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-stone/10">
            <ActionItemHistory item={item} />
          </div>
        </div>
      )}
    </div>
  )
}

/** Completed items: same table row, struck through, clicking reopens. */
function CompletedActionItemRow({ item, readOnly, onReopen }: {
  item: ActionItem
  readOnly?: boolean
  onReopen: (item: ActionItem) => void
}) {
  const ownerTriggerRef = useRef<HTMLButtonElement>(null)

  return (
    <div data-action-item-row="true" id={`meeting-action-item-${item.id}`}>
      <div
        onClick={() => !readOnly && onReopen(item)}
        className={`${ACTION_ITEM_ROW_GRID} py-2.5 border-b border-gray-100 transition-colors group ${readOnly ? '' : 'cursor-pointer hover:bg-[#F8F9FA]'}`}
        style={{ opacity: 0.5 }}
      >
        <input
          type="checkbox"
          checked={true}
          onClick={e => e.stopPropagation()}
          onChange={() => !readOnly && onReopen(item)}
          disabled={readOnly}
          aria-label={`Reopen ${item.title}`}
          className="w-5 h-5 rounded border-stone/30 text-[#15532E] focus:ring-[#15532E]/20 flex-shrink-0 disabled:opacity-50"
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
        />
        <ActionItemTitleCell item={item} editable={false} />
        <ActionItemOwnerCell
          item={item}
          members={[]}
          open={false}
          onToggle={() => {}}
          onClose={() => {}}
          onSelect={() => {}}
          triggerRef={ownerTriggerRef}
          editable={false}
        />
        <ActionItemDueDateCell item={item} editable={false} emptyLabel="No due date" />
        <ActionItemCreatedDateCell item={item} />
        <ActionItemSourceCell item={item} />
        <div className="flex justify-end">
          <svg style={{ width: '16px', height: '16px', color: '#15532E' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
    </div>
  )
}
